import { useCallback, useEffect, useRef, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { TopBar } from './TopBar';
import { Sidebar } from './Sidebar';
import { ComparisonFab } from '../ComparisonFab/ComparisonFab';
import { AnalyticsConsent } from '../AnalyticsConsent/AnalyticsConsent';

export function AppLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();
  const mainRef = useRef<HTMLElement>(null);
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const closeSidebar = useCallback(() => setSidebarOpen(false), []);
  const toggleSidebar = useCallback(() => setSidebarOpen((open) => !open), []);

  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const main = mainRef.current;
    if (!main) return;
    main.scrollTo({ top: 0, left: 0 });

    let observer: MutationObserver | undefined;

    const focusHeading = () => {
      const heading = main.querySelector<HTMLElement>('h1');
      if (!heading) return false;

      heading.tabIndex = -1;
      heading.focus({ preventScroll: true });
      return true;
    };

    const frame = window.requestAnimationFrame(() => {
      if (focusHeading()) return;

      main.focus({ preventScroll: true });
      observer = new MutationObserver(() => {
        if (focusHeading()) observer?.disconnect();
      });
      observer.observe(main, { childList: true, subtree: true });
    });

    return () => {
      window.cancelAnimationFrame(frame);
      observer?.disconnect();
    };
  }, [location.pathname]);

  return (
    <div className="flex h-screen overflow-hidden flex-col bg-[var(--console-canvas)] text-slate-950">
      <a
        href="#main-content"
        className="sr-only z-[100] rounded bg-white px-4 py-2 text-sm font-semibold text-blue-700 shadow focus:not-sr-only focus:fixed focus:left-3 focus:top-3"
      >
        Skip to main content
      </a>
      <TopBar
        onMenuToggle={toggleSidebar}
        sidebarOpen={sidebarOpen}
        menuButtonRef={menuButtonRef}
      />
      <AnalyticsConsent />

      <div className="flex flex-1 overflow-hidden">
        <Sidebar open={sidebarOpen} onClose={closeSidebar} triggerRef={menuButtonRef} />

        <main
          ref={mainRef}
          id="main-content"
          tabIndex={-1}
          className="min-w-0 flex-1 overflow-y-auto bg-[var(--console-canvas)] outline-none"
        >
          <div className="mx-auto w-full max-w-[1600px] px-4 py-5 sm:px-6 lg:px-8 lg:py-8">
            <Outlet />
          </div>
        </main>
      </div>

      <div id="comparison-fab-layer">
        <ComparisonFab />
      </div>
    </div>
  );
}
